import React, { useCallback, useEffect, useState } from "react";
import { Container, Table, Button, Form, Spinner, Modal } from "react-bootstrap";
import { useNavigate, useParams } from "react-router-dom";
import { SetCookie } from "../../common/CookieHelper";
import { useGlobal } from "../../contexts/GlobalContext";
import { Utils } from "../../common/Utils";
import { CartItem } from "../../model/CartModel";
import { ProductGateway } from "../../gateways/ProductGateway";
import { OrderGateway } from "../../gateways/OrderGateway";
import { CartGateway } from "../../gateways/CartGateway";
import { Trash } from "lucide-react";
import { GetCartCookieKey } from "../../gateways/SiteGateway";

enum FlagState {
    Show = "show",
    Hide = "hide",
    Complete = "complete",
}

const Cart: React.FC = () => {
    const [enrichedCart, setEnrichedCart] = useState<CartItem[]>();
    const { cart, updateCart, settings, user } = useGlobal();
    const [previousOrder, setPreviousOrder] = useState<CartItem[]>([]);
    const [showModal, setShowModal] = useState<FlagState>(FlagState.Hide);
    const [loading, setLoading] = useState<boolean>(true);
    const { orderId } = useParams(); // Get orderId from URL
    const navigate = useNavigate();

    const totalAmount = enrichedCart ? enrichedCart.reduce((sum, item) => sum + item.quantity * item.price, 0) : 0;
    const vatAmount = settings && settings.vatPercentage && settings.vatPercentage > 0 ? (totalAmount - totalAmount / (1 + settings.vatPercentage / 100)) : 0;

    const persistCart = useCallback((updatedCart: CartItem[]) => {
        if (user) CartGateway.SaveCart({...cart, cartItems: updatedCart}, user);
        else SetCookie(GetCartCookieKey, JSON.stringify({...cart, cartItems: updatedCart}), 7);
    }, [user, cart]);

    useEffect(() => {

        const fetchCart = async () => {

            setLoading(true);
            // Fetch product details from Firestore & filter out inactive products
            const enrichedCartPromise = await Promise.all(
                cart.cartItems.map(async (item) => {

                    const productData = await ProductGateway.GetProduct(item.id);
                    if (productData) {
                        // Only include active products in the cart
                        if (productData.status === "Active") {
                            return {
                                ...item, imageUrl: productData.imageUrl, price: Number(productData.price) + Object.entries(item.attributes).reduce((sum, [name, value]) => {
                                    return sum + ProductGateway.GetAttributePrice(productData, name, value);
                                }, 0)
                            };
                        }
                    }
                    return item;
                })
            );

            setEnrichedCart(enrichedCartPromise.filter(Boolean));

            // If orderId exists, fetch previous order
            if (orderId) {
                const orderData = await OrderGateway.GetOrder(orderId);
                if (orderData) {
                    const orderItems = orderData.cartItems || [];
                    var cartItems = orderItems.map(oi => { return { ...oi, price: Number(oi.priceAfterTax) } });
                    if (cart.cartItems.length > 0) {
                        if (showModal === FlagState.Hide) {
                            setPreviousOrder(cartItems);
                            setShowModal(FlagState.Show);
                        }
                    }
                    else {
                        persistCart(cartItems);
                        updateCart();
                        setShowModal(FlagState.Complete);
                    }
                }
            }
            setLoading(false);
        };

        fetchCart();
    }, [cart, orderId, showModal, persistCart, updateCart]);

    if ((loading || !settings) && enrichedCart?.length === 0) {
        return (
            <Container className="mt-4 text-center">
                <Spinner animation="border" variant="primary" role="status">
                    <span className="visually-hidden">Loading...</span>
                </Spinner>
                <p>Fetching order summary...</p>
            </Container>
        );
    }

    const saveCart = (updatedCart: CartItem[]) => {
        setEnrichedCart(updatedCart);
        persistCart(updatedCart);

        updateCart();
    };

    const updateQuantity = (id: string, quantity: number) => {
        if (quantity < 1 || !enrichedCart) return;
        const updatedCart = enrichedCart.map(item => (item.id === id ? { ...item, quantity } : item));
        saveCart(updatedCart);
    };

    const removeItem = (id: string) => {
        if (enrichedCart) {
            const updatedCart = enrichedCart.filter(item => ProductGateway.GetKey(item.id, item.attributes) !== id);
            saveCart(updatedCart);
        }
    };

    const handleCombine = () => {
        if (enrichedCart) {
            const mergedCart = [...enrichedCart];

            previousOrder.forEach((prevItem) => {
                const existingItem = mergedCart.find((item) => ProductGateway.GetKey(item.id, item.attributes) === ProductGateway.GetKey(prevItem.id, prevItem.attributes));

                if (existingItem) {
                    existingItem.quantity += prevItem.quantity; // Merge quantities
                } else {
                    mergedCart.push(prevItem); // Add new items
                }
            });

            saveCart(mergedCart);
        }
        setShowModal(FlagState.Complete);
    };

    const handleRemove = () => {
        setShowModal(FlagState.Complete);
        saveCart(previousOrder);
    };

    const handleCheckout = () => {
        if (settings && !settings.allowEmailLogin && !user) {
            navigate(Utils.GetUrl("/login/checkout"));
            return;
        }
        navigate(Utils.GetUrl("/checkout"));
    }

    return (
        <Container className="main-container mt-4">
            <h2>Order Summary</h2>

            {/* Modal for handling repeat orders */}
            <Modal show={showModal === FlagState.Show} onHide={() => setShowModal(FlagState.Complete)} centered>
                <Modal.Header closeButton>
                    <Modal.Title>Manage Existing Cart</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    <p>You have existing items in your cart. What would you like to do?</p>
                </Modal.Body>
                <Modal.Footer>
                    <Button variant="primary" onClick={handleCombine}>Combine Items</Button>
                    <Button variant="danger" onClick={handleRemove}>Remove Existing</Button>
                    <Button variant="secondary" onClick={() => setShowModal(FlagState.Complete)}>Cancel</Button>
                </Modal.Footer>
            </Modal>


            {(enrichedCart && enrichedCart.length === 0 ? (
                <p className="text-center">Your cart is empty</p>
            ) : (<>
                <Table striped bordered hover responsive>
                    {settings.hasPrice ?
                        (<colgroup>
                            <col style={{ width: "50%" }} />{/* Product column */}
                            <col style={{ width: "20%" }} />{/* Price column */}
                            <col style={{ width: "20%" }} />{/* Quantity column */}
                            <col style={{ width: "10%" }} />{/* Actions column */}
                        </colgroup>) :
                        (<colgroup>
                            <col style={{ width: "70%" }} />{/* Product column */}
                            <col style={{ width: "20%" }} />{/* Quantity column */}
                            <col style={{ width: "10%" }} />{/* Actions column */}
                        </colgroup>)
                    }
                    <thead className="table-dark">
                        <tr>
                            <th>Product</th>
                            <th>Qty</th>
                            {settings.hasPrice &&
                                <th className="text-end">Total</th>
                            }
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {enrichedCart && enrichedCart.map(item => (
                            <tr key={item.id}>
                                <td>
                                    <div className="d-flex align-items-center">
                                        {item.imageUrl && (
                                            <Button variant="link" className="p-0 border-0" onClick={() => navigate(Utils.GetUrl(`/product/${item.id}`))}>
                                                <img src={item.imageUrl} alt={item.name} width="50" height="50" className="me-2" />
                                            </Button>
                                        )}
                                        <div>
                                            <a href={Utils.GetUrl(`/product/${item.id}`)} style={{ padding: "12px" }}>{item.name}</a>
                                            {/* <Button variant="link" className="text-decoration-none" onClick={() => navigate(`/product/${item.id}`)}>
                                                    {item.name}
                                                </Button> */}
                                            <br />
                                            <small className="text-muted" style={{ padding: "12px" }}>
                                                {Object.entries(item.attributes).map(([key, value]) => `${key}: ${value}`).join(", ")}
                                            </small>
                                        </div>
                                    </div>
                                </td>
                                <td className="text-start align-middle">
                                    <Form.Control
                                        type="number"
                                        value={item.quantity}
                                        min={1}
                                        onChange={(e) => updateQuantity(item.id, parseInt(e.target.value, 10))}
                                    />
                                </td>
                                {settings.hasPrice &&
                                    <td className="text-end align-middle">
                                        {Utils.FormatCurrency((item.price * item.quantity), settings.currency)}
                                    </td>
                                }
                                <td className="text-center align-middle">
                                    {/* Visible on mobile (sm and down) */}
                                    <Button
                                        variant="danger"
                                        className="p-0 border-0 d-sm-none"
                                        style={{ background: "transparent" }}
                                        onClick={() => removeItem(ProductGateway.GetKey(item.id, item.attributes))}
                                    >
                                        <Trash size={30} style={{cursor: "pointer", color:"#555555"}}></Trash>                                                  
                                    </Button>

                                    {/* Visible on larger screens */}
                                    <Button
                                        variant="danger"
                                        className="d-none d-sm-inline-block"
                                        onClick={() => removeItem(ProductGateway.GetKey(item.id, item.attributes))}
                                    >
                                        Remove
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
                {settings.hasPrice && <>
                    <hr />
                    <div className="text-end">
                        <p>VAT ({settings.vatPercentage}%): <strong>{Utils.FormatCurrency(vatAmount, settings.currency)}</strong></p>
                        <h5>Total: <strong>{Utils.FormatCurrency(totalAmount, settings.currency)}</strong></h5>
                    </div>
                    <hr />
                </>}
                <div className="text-end">
                    <Button className="mt-3" onClick={handleCheckout}>
                        Checkout
                    </Button>
                </div>
            </>)
            )}

        </Container>
    );
};

export default Cart;